import { StatusEnum } from './../../enums/status';
import { ISegment } from '@monorepo/base/src/components/segments/segments';
import { FilterModel } from '@monorepo/controlled/src/models/filter.model';
import { BaseListStore } from '@monorepo/controlled/src/stores/base-list.store';
import { FilterStore } from '@monorepo/controlled/src/stores/filter.store';
import { HttpStore } from '@monorepo/controlled/src/stores/http.store';
import { capitalCase } from 'change-case';
import { action, makeAutoObservable, observable } from 'mobx';
import { CampaignsApi } from '../../apis/campaigns.api';
import { filterPrototypesByProp } from '../../components/filters';
import { IPerformanceOptions, PerformancesColumns } from '../../hooks/apis/use-reports-list';
import { CampaignModel } from '../../models/campaign.model';
import { PerformancePayload, PerformanceModel } from '../../models/performance.model';
import { CampaignCrudStore } from './campaign-crud.store';
import { StatusFilterPrototype } from '../../components/filters/status.filter';
import { SettingsStore } from '@monorepo/controlled/src/stores/settings.store';
import { TableStore } from '@monorepo/controlled/src/stores/table.store';
import { Row } from '@tanstack/react-table';

// every enum here is the path withtout / and ids
// for example, advertiser/1/campaigns will transform to CampaignFiltersStoreKeys.Advertiser
export enum CampaignFiltersStoreKeys {
	Campaigns = 'campaigns',
	Advertiser = 'advertiserscampaigns',
	Account = 'accountscampaigns',
	Clicks = 'clickscampaigns',
	Conversions = 'conversionscampaigns',
}

export class CampaignStore {
	crud = new CampaignCrudStore();
	list = new BaseListStore<CampaignModel>({
		listFunc: CampaignsApi.list,
		model: CampaignModel,
	});
	reports = new HttpStore<IPerformanceOptions, PerformancePayload>({
		httpFunc: CampaignsApi.reports,
		model: PerformancePayload,
	});
	metrics = new HttpStore<IPerformanceOptions, PerformancePayload>({
		httpFunc: CampaignsApi.metrics,
		model: PerformancePayload,
	});

	insights = new HttpStore<IPerformanceOptions, PerformancePayload>({
		httpFunc: CampaignsApi.insights,
		model: PerformancePayload,
	});

	segments: Set<ISegment<CampaignModel, PerformanceModel, PerformancesColumns>>;

	sortedRows: Row<CampaignModel>[] = [];

	settingsStore: SettingsStore;

	constructor(settingsStore: SettingsStore) {
		makeAutoObservable(this, {
			crud: observable,
			list: observable,
			metrics: observable,
			insights: observable,
			segments: observable,
			sortedRows: observable,
			setSegments: action.bound, // https://mobx.js.org/actions.html#actionbound prevents cannot set property of undefined
			reports: observable,
		});
		this.segments = new Set<ISegment<CampaignModel, PerformanceModel, PerformancesColumns>>([]);
		this.settingsStore = settingsStore;
		this.createDefaultFilters();
	}

	createDefaultFilters() {
		// set filters for all campaigns pages
		const defaultFilters = new Map([
			[
				1,
				new FilterModel({
					index: 1,
					label: `Status is ${capitalCase(StatusEnum.ACTIVE)}`,
					value: [StatusEnum.ACTIVE],
					prototype: StatusFilterPrototype,
				}),
			],
		]);

		Object.values(CampaignFiltersStoreKeys).map((key: CampaignFiltersStoreKeys) => {
			if (!this.settingsStore.getPages().get(key)) {
				this.settingsStore.getPages().set(key, { tableStore: new TableStore() });
			}
			const campaignFilterByKey = new FilterStore({
				key,
				currentFilters: defaultFilters,
				filterPrototypesByProp,
			});
			this.settingsStore.getPages().get(key)?.tableStore.setFiltersStore(key, campaignFilterByKey);
		});
	}

	getCrud() {
		return this.crud;
	}

	getListStore() {
		return this.list;
	}

	getReportsStore() {
		return this.reports;
	}

	getMetricsStore() {
		return this.metrics;
	}
	getInsightsStore() {
		return this.insights;
	}

	setSegments(segments: Set<ISegment<CampaignModel, PerformanceModel, PerformancesColumns>>): void {
		this.segments = segments;
	}

	getSegments(): Set<ISegment<CampaignModel, PerformanceModel, PerformancesColumns>> {
		return this.segments;
	}

	getSortedRows(): Row<CampaignModel>[] {
		return this.sortedRows;
	}

	setSortedRows(sortedRows: Row<CampaignModel>[]): void {
		this.sortedRows = sortedRows;
	}
}
